import { isWithinOsSendWindow } from "./os-notification-schedule";

export const OS_NOTIFICATION_KINDS = [
  "new_message",
  "email_reply",
  "new_deal",
  "stage_change",
  "intake_submission",
  "intake_stalled",
  "data_room_access_request",
  "signature_expired",
  "signature_declined",
  "signature_signed",
  "valuation_failed",
  "research_review",
  "deal_stalled",
  "buyer_prequalification_stalled",
  "lead_lost",
  "campaign_failed",
  "system_degraded",
] as const;

export type OsNotificationKind = (typeof OS_NOTIFICATION_KINDS)[number];

/**
 * NONE: never emailed. EXISTING: another pipeline already emails it.
 * IMMEDIATE: its own email as soon as allowed. DIGEST: folded into the Monday
 * pipeline summary, never its own email.
 */
export type OsNotificationEmailMode =
  | "NONE"
  | "EXISTING"
  | "IMMEDIATE"
  | "DIGEST";

export type OsNotificationPreference =
  | "osActionAlerts"
  | "osUrgentAlerts"
  | "osStaleAlerts"
  | "osBuyBoxMatchEmails";

export type OsNotificationPolicy = {
  emailMode: OsNotificationEmailMode;
  preference: OsNotificationPreference | null;
  initialDelayMinutes: number;
  /** Unread IMMEDIATE alerts get one "Reminder:" re-send after this many hours. */
  escalationDelayHours: number | null;
};

export const OS_NOTIFICATION_EMAIL_KINDS = [
  "email_reply",
  "new_deal",
  "intake_submission",
  "intake_stalled",
  "data_room_access_request",
  "signature_expired",
  "signature_declined",
  "signature_signed",
  "valuation_failed",
  "research_review",
  "deal_stalled",
  "buyer_prequalification_stalled",
  "lead_lost",
  "campaign_failed",
  "system_degraded",
] as const;

/** Kinds folded into the Monday summary instead of sending their own email. */
export const OS_NOTIFICATION_DIGEST_KINDS = [
  "deal_stalled",
  "intake_stalled",
  "buyer_prequalification_stalled",
] as const;

/**
 * Kinds a broker who has never signed in may still receive immediately:
 * a person is waiting on them. Everything else waits for first sign-in.
 */
export const OS_NOTIFICATION_COLD_RECIPIENT_KINDS = [
  "new_deal",
  "email_reply",
  "intake_submission",
  "data_room_access_request",
] as const;

/** Max IMMEDIATE emails per recipient per local day; overflow joins the Monday summary. */
export const OS_IMMEDIATE_EMAIL_DAILY_CAP = 5;

/**
 * An unsent immediate email older than this is dropped. Overnight delay is
 * fine (quiet hours run until 8am). A day later, "X just happened" is false.
 */
export const OS_IMMEDIATE_EMAIL_MAX_AGE_HOURS = 36;

export {
  OS_SEND_WINDOW,
  getLocalDateKey,
  getLocalHour,
  getLocalIsoWeekKey,
  getLocalWeekday,
  isWithinOsSendWindow,
  shouldSendBuyBoxMatchEmailNow,
  shouldSendOsDigestNow,
} from "./os-notification-schedule";

const DEFAULT_POLICY: OsNotificationPolicy = {
  emailMode: "NONE",
  preference: null,
  initialDelayMinutes: 0,
  escalationDelayHours: null,
};

const immediate = (
  preference: OsNotificationPreference,
  escalationDelayHours: number | null = null,
): OsNotificationPolicy => ({
  emailMode: "IMMEDIATE",
  preference,
  initialDelayMinutes: 0,
  escalationDelayHours,
});

const digest = (initialDelayMinutes = 0): OsNotificationPolicy => ({
  emailMode: "DIGEST",
  preference: "osStaleAlerts",
  initialDelayMinutes,
  escalationDelayHours: null,
});

const POLICIES: Record<OsNotificationKind, OsNotificationPolicy> = {
  new_message: { ...DEFAULT_POLICY, emailMode: "EXISTING" },
  email_reply: immediate("osActionAlerts"),
  new_deal: immediate("osActionAlerts"),
  stage_change: DEFAULT_POLICY,
  intake_submission: immediate("osActionAlerts", 48),
  intake_stalled: digest(24 * 60),
  data_room_access_request: immediate("osActionAlerts", 48),
  signature_expired: immediate("osActionAlerts"),
  signature_declined: immediate("osActionAlerts"),
  signature_signed: immediate("osActionAlerts"),
  valuation_failed: immediate("osUrgentAlerts"),
  research_review: immediate("osActionAlerts"),
  deal_stalled: digest(),
  buyer_prequalification_stalled: digest(24 * 60),
  lead_lost: immediate("osUrgentAlerts"),
  campaign_failed: immediate("osUrgentAlerts"),
  system_degraded: immediate("osUrgentAlerts"),
};

/** Kinds that get their own email (as opposed to the Monday summary). */
export const OS_IMMEDIATE_EMAIL_KINDS: string[] = OS_NOTIFICATION_KINDS.filter(
  (kind) => POLICIES[kind].emailMode === "IMMEDIATE",
);

/** Immediate kinds that get one "Reminder:" re-send, with the shortest delay among them. */
export const OS_ESCALATING_EMAIL_KINDS: string[] =
  OS_IMMEDIATE_EMAIL_KINDS.filter(
    (kind) => POLICIES[kind as OsNotificationKind].escalationDelayHours != null,
  );

export const OS_MIN_ESCALATION_DELAY_HOURS: number = Math.min(
  ...OS_ESCALATING_EMAIL_KINDS.map(
    (kind) =>
      POLICIES[kind as OsNotificationKind].escalationDelayHours ?? Infinity,
  ),
);

/**
 * Unsent immediate mail that would present old or already-seen work as new.
 * Callers mark the delivery aside so it leaves the send queue.
 */
export function shouldRetireUnsentImmediateEmail(
  kind: string,
  state: Pick<
    OsNotificationEmailState,
    "createdAt" | "readAt" | "emailSentAt" | "now"
  >,
): boolean {
  const policy = getOsNotificationPolicy(kind);
  if (policy.emailMode !== "IMMEDIATE" || state.emailSentAt) return false;
  if (state.readAt) return true;
  const now = state.now ?? new Date();
  const ageMinutes = (now.getTime() - state.createdAt.getTime()) / 60000;
  return ageMinutes > OS_IMMEDIATE_EMAIL_MAX_AGE_HOURS * 60;
}

export function getOsNotificationPolicy(kind: string): OsNotificationPolicy {
  return POLICIES[kind as OsNotificationKind] ?? { ...DEFAULT_POLICY };
}

export type OsNotificationEmailState = {
  createdAt: Date;
  readAt: Date | null;
  emailSentAt: Date | null;
  emailEscalatedAt: Date | null;
  emailDigestedAt?: Date | null;
  emailAttemptedAt?: Date | null;
  now?: Date;
  osActionAlerts: boolean;
  osUrgentAlerts: boolean;
  osStaleAlerts: boolean;
  /** Set when the user clicked "Stop Venturu OS emails". */
  osEmailsStoppedAt?: Date | null;
  /** Per-deal preferences set from email actions. */
  dealMutedAt?: Date | null;
  dealSnoozedUntil?: Date | null;
  /** False for passwordless brokers who never opened a magic link. */
  recipientHasSignedIn?: boolean;
  /** IMMEDIATE emails already sent to this recipient today (local day). */
  sentTodayCount?: number;
  /** Recipient's current local hour (0-23). Omit to skip the send-window check. */
  localHour?: number;
};

export type OsNotificationEmailStage = "INITIAL" | "ESCALATION" | "DIGEST";

/**
 * Decide what, if anything, to email for one delivery.
 *
 * Order of checks: kind is emailable → recipient has not stopped emails →
 * category preference on → deal not muted or snoozed → cold recipients only
 * get human-triggered kinds → an immediate alert the broker already opened,
 * or that is already a day old, is dropped → DIGEST kinds wait for the Monday
 * summary → IMMEDIATE kinds respect the daily cap (overflow joins that
 * summary) and the send window → one reminder 48 hours after the email we
 * actually sent, for unread alerts whose policy allows it.
 */
export function getOsNotificationEmailStage(
  kind: string,
  state: OsNotificationEmailState,
): OsNotificationEmailStage | null {
  const policy = getOsNotificationPolicy(kind);
  if (policy.emailMode === "NONE" || policy.emailMode === "EXISTING") {
    return null;
  }
  if (state.osEmailsStoppedAt) return null;

  const preferenceEnabled =
    policy.preference == null ||
    state[policy.preference as keyof OsNotificationEmailState] === true;
  if (!preferenceEnabled) return null;

  const now = state.now ?? new Date();
  if (state.dealMutedAt) return null;
  if (state.dealSnoozedUntil && state.dealSnoozedUntil > now) return null;

  const isCold = state.recipientHasSignedIn === false;
  if (
    isCold &&
    !(OS_NOTIFICATION_COLD_RECIPIENT_KINDS as readonly string[]).includes(kind)
  ) {
    return null;
  }

  const ageMinutes = (now.getTime() - state.createdAt.getTime()) / 60000;
  if (ageMinutes < policy.initialDelayMinutes) return null;

  const alreadyEmailed =
    state.emailSentAt != null || state.emailDigestedAt != null;

  if (policy.emailMode === "DIGEST") {
    return alreadyEmailed || state.readAt ? null : "DIGEST";
  }

  if (!alreadyEmailed) {
    if (shouldRetireUnsentImmediateEmail(kind, state)) return null;
    if ((state.sentTodayCount ?? 0) >= OS_IMMEDIATE_EMAIL_DAILY_CAP) {
      return "DIGEST";
    }
    if (state.localHour != null && !isWithinOsSendWindow(state.localHour)) {
      return null;
    }
    return "INITIAL";
  }

  const sentAt = state.emailSentAt;
  const sinceSentMinutes =
    sentAt == null ? null : (now.getTime() - sentAt.getTime()) / 60000;
  if (
    sentAt != null &&
    sinceSentMinutes != null &&
    state.emailEscalatedAt == null &&
    state.readAt == null &&
    policy.escalationDelayHours != null &&
    sinceSentMinutes >= policy.escalationDelayHours * 60
  ) {
    if (state.localHour != null && !isWithinOsSendWindow(state.localHour)) {
      return null;
    }
    return "ESCALATION";
  }

  return null;
}
